/**
 * /auto-captions — Mute words (curse-mute / NDA-mute)
 * ---------------------------------------------------------------
 * Mark transcript words as MUTED so the corresponding audio range
 * is silenced (volume=0) in the burned MP4. Subtitles + video stay
 * intact — only the audio for those moments is dropped to silence.
 *
 * Use-cases:
 *   - Curse-mute: bleep individual swear-words without a full re-cut.
 *   - NDA-mute: silence proper nouns / numbers / addresses spoken
 *     on-camera before publishing a Q&A clip.
 *
 * What this module is and isn't:
 *   IS:  a tiny per-word state store + an FFmpeg "volume=0:enable=..."
 *        expression builder. Pure browser, no worker, no server.
 *   IS NOT: the burn pipeline. The pipeline (auto-captions-pipeline.js)
 *           reads buildFFmpegVolumeFilter() and injects it into the
 *           audio branch of the filter graph.
 *
 * Composition with edit-by-transcript / magic-cut:
 *   We operate on the UNCUT timeline. The pipeline applies mute on
 *   the source timeline FIRST (inside the audio chain that already
 *   carries aselect cuts via edit-by-transcript). The _muted flag on
 *   each word is carried forward by ebt.mapWordsToCutTimeline so the
 *   ASS subtitle still highlights the muted region correctly.
 *
 *   IMPORTANT: getMuteList() reports ranges in SOURCE time, not cut
 *   time. The pipeline is responsible for placing the volume filter
 *   BEFORE the aselect filter in the audio chain.
 *
 * Public API on window.__acMuteWord:
 *   .muteWord(idx)              → mark state.words[idx]._muted = true, re-render
 *   .unmuteWord(idx)            → unset _muted, re-render
 *   .toggleWord(idx)            → flip _muted
 *   .reset()                    → clear every _muted flag
 *   .hasMutes()                 → boolean — any words currently muted?
 *   .getMuteList()              → sorted, non-overlapping merged ranges
 *                                 [{start, end}] in SECONDS, source timeline.
 *   .buildFFmpegVolumeFilter()  → "volume=0:enable=between(t,a,b)+between(t,c,d)"
 *                                 or null when no mutes. Ready to drop into the
 *                                 audio filter chain.
 *   .installEditorHooks()       → idempotently wires right-click + hover-icon
 *                                 toggle on .ac-ed-word, plus the footer chip.
 *
 * Merge tolerance:
 *   Adjacent _muted words within MERGE_EPSILON (50 ms) collapse into
 *   a single enable() clause. Without this, ffmpeg's volume filter
 *   evaluates one OR-clause per word, ballooning the filter string
 *   on long mute runs and producing audible micro-clicks between
 *   adjacent zeroed samples.
 */
(function(){
    'use strict';

    var MERGE_EPSILON = 0.05; // seconds — gap below this merges adjacent mutes

    /* --------- internal helpers --------- */

    function getState(){
        return window.__acState || null;
    }

    function getWords(){
        var st = getState();
        return (st && Array.isArray(st.words)) ? st.words : [];
    }

    /* Walk state.words once, build a sorted non-overlapping list of
       [start, end] mute ranges. Mirrors the edit-by-transcript shape
       so the pipeline can use the same coordinate system. */
    function collectMutes(){
        var words = getWords();
        /* Collect every muted word as a raw range first — we sort BEFORE
           merging so out-of-order words (post-speaker-relabel / smart-break)
           don't break the adjacency test. Doing it the other way round means
           a word at t=1 following a word at t=5 looks "adjacent" because the
           gap (1 - 5) = -4 is still ≤ MERGE_EPSILON. */
        var raw = [];
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w || !w._muted) continue;
            var s = typeof w.start === 'number' ? w.start : 0;
            var e = typeof w.end   === 'number' ? w.end   : s;
            if (e < s) { var tmp = s; s = e; e = tmp; }
            raw.push({ start: s, end: e });
        }
        if (!raw.length) return [];
        raw.sort(function(a, b){ return a.start - b.start; });
        var out = [];
        for (var j = 0; j < raw.length; j++){
            if (out.length && raw[j].start - out[out.length - 1].end <= MERGE_EPSILON) {
                out[out.length - 1].end = Math.max(out[out.length - 1].end, raw[j].end);
            } else {
                out.push(raw[j]);
            }
        }
        return out;
    }

    /* Build the FFmpeg "volume=0:enable='between(t,a,b)+between(t,c,d)'"
       expression. Returns null when no mutes (caller should skip the
       filter rather than emit "volume=0:enable=0" which always passes). */
    function buildVolumeFilter(){
        var ranges = collectMutes();
        if (!ranges.length) return null;
        var parts = [];
        for (var i = 0; i < ranges.length; i++){
            var r = ranges[i];
            parts.push('between(t,' + r.start.toFixed(3) + ',' + r.end.toFixed(3) + ')');
        }
        /* Quoted enable= so ffmpeg parses the '+' as expr-OR, not arg-separator. */
        return "volume=0:enable='" + parts.join('+') + "'";
    }

    /* --------- UI: hover-icon + context-menu + chip --------- */

    var hooksInstalled = false;
    var chipEl = null;
    var hoverIconEl = null;

    function ensureChip(){
        if (typeof document === 'undefined') return null;
        if (chipEl && document.body.contains(chipEl)) return chipEl;
        var burnBtn = document.getElementById('ac-export-burn');
        if (!burnBtn || !burnBtn.parentNode) return null;
        chipEl = document.createElement('div');
        chipEl.id = 'ac-mute-chip';
        chipEl.className = 'ac-mute-chip';
        chipEl.setAttribute('role', 'status');
        chipEl.setAttribute('aria-live', 'polite');
        chipEl.style.cssText = [
            'display:none',
            'margin:8px 0',
            'padding:8px 12px',
            'border-radius:8px',
            'background:rgba(125,160,255,0.12)',
            'border:1px solid rgba(125,160,255,0.5)',
            'color:var(--text-primary,#fff)',
            'font-size:13px',
            'gap:10px',
            'align-items:center',
            'justify-content:space-between',
        ].join(';');
        var label = document.createElement('span');
        label.className = 'ac-mute-chip-label';
        var undo = document.createElement('button');
        undo.type = 'button';
        undo.textContent = (window.I18N && window.I18N.mute_word_unmute_all) || 'Unmute all';
        undo.className = 'ac-mute-chip-undo';
        undo.setAttribute('aria-label', (window.I18N && window.I18N.mute_word_unmute_all_aria) || 'Unmute all muted words');
        undo.style.cssText = 'background:transparent;border:1px solid currentColor;color:inherit;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px';
        undo.addEventListener('click', function(){ api.reset(); });
        chipEl.appendChild(label);
        chipEl.appendChild(undo);
        burnBtn.parentNode.insertBefore(chipEl, burnBtn);
        return chipEl;
    }

    function renderChip(){
        var el = ensureChip();
        if (!el) return;
        var words = getWords();
        var count = 0;
        for (var i = 0; i < words.length; i++) if (words[i] && words[i]._muted) count++;
        if (!count) { el.style.display = 'none'; return; }
        el.style.display = 'flex';
        var label = el.querySelector('.ac-mute-chip-label');
        if (label) {
            var tmpl = (window.I18N && window.I18N.mute_word_chip) || ':count muted word(s)';
            label.textContent = tmpl.replace(':count', String(count));
        }
    }

    /* Apply / remove visual mute styling on every editor word span.
       The edit-by-transcript module owns the spans (paints them via
       its MutationObserver), so we just toggle a class + aria-pressed
       on top — no DOM rebuild conflicts. */
    function repaintEditor(){
        if (typeof document === 'undefined') return;
        var editor = document.getElementById('ac-editor');
        if (!editor) return;
        var st = getState();
        if (!st || !st.words) return;
        var nodes = editor.querySelectorAll('.ac-ed-word');
        for (var i = 0; i < nodes.length; i++){
            var el = nodes[i];
            var idx = parseInt(el.getAttribute('data-word-idx'), 10);
            if (isNaN(idx)) continue;
            var w = st.words[idx];
            if (!w) continue;
            if (w._muted) {
                el.classList.add('is-muted');
                el.setAttribute('data-muted', '1');
            } else {
                el.classList.remove('is-muted');
                el.removeAttribute('data-muted');
            }
        }
    }

    function injectStyles(){
        if (typeof document === 'undefined') return;
        if (document.getElementById('ac-mute-styles')) return;
        var style = document.createElement('style');
        style.id = 'ac-mute-styles';
        style.textContent = [
            /* Muted = strike-through + reduced opacity. Pair with
               edit-by-transcript: a muted word that is ALSO deleted
               still reads as deleted (strike-through wins either way). */
            '.ac-ed-word.is-muted{text-decoration:line-through;opacity:.55;text-decoration-color:rgba(125,160,255,0.9);text-decoration-thickness:2px}',
            '.ac-ed-word.is-muted:focus{outline:1px solid rgba(125,160,255,0.7)}',
            /* Hover icon: small mute glyph appearing on word hover/focus.
               Position absolute relative to the word; pointer-events:auto
               so the icon itself is clickable. */
            '.ac-mute-hover-icon{position:absolute;display:none;width:18px;height:18px;line-height:18px;text-align:center;border-radius:50%;background:rgba(20,24,40,0.92);color:#fff;font-size:12px;cursor:pointer;border:1px solid rgba(125,160,255,0.5);user-select:none;pointer-events:auto;z-index:20}',
            '.ac-mute-hover-icon[data-state="muted"]{background:rgba(125,160,255,0.9)}',
            '.ac-mute-chip{display:flex}',
            '.ac-mute-chip-undo:hover{background:rgba(255,255,255,0.08)}',
        ].join('\n');
        document.head.appendChild(style);
    }

    function ensureHoverIcon(){
        if (typeof document === 'undefined') return null;
        if (hoverIconEl && document.body.contains(hoverIconEl)) return hoverIconEl;
        hoverIconEl = document.createElement('div');
        hoverIconEl.className = 'ac-mute-hover-icon';
        hoverIconEl.setAttribute('role', 'button');
        hoverIconEl.setAttribute('tabindex', '-1');
        hoverIconEl.textContent = '🔇'; /* 🔇 */
        hoverIconEl.addEventListener('mousedown', function(e){
            /* mousedown not click — click fires AFTER the word loses focus and
               the icon hides itself, which would null the target reference. */
            e.preventDefault(); e.stopPropagation();
            var idx = hoverIconEl.__targetIdx;
            if (typeof idx === 'number') api.toggleWord(idx);
        });
        document.body.appendChild(hoverIconEl);
        return hoverIconEl;
    }

    function showHoverIcon(wordEl){
        if (!wordEl) return;
        var icon = ensureHoverIcon();
        var rect = wordEl.getBoundingClientRect();
        var idx = parseInt(wordEl.getAttribute('data-word-idx'), 10);
        if (isNaN(idx)) return;
        var st = getState();
        var muted = !!(st && st.words && st.words[idx] && st.words[idx]._muted);
        icon.__targetIdx = idx;
        icon.style.display = 'block';
        icon.style.top  = (rect.top + window.scrollY - 20) + 'px';
        icon.style.left = (rect.right + window.scrollX - 6) + 'px';
        icon.setAttribute('data-state', muted ? 'muted' : 'unmuted');
        icon.setAttribute('aria-label',
            muted
                ? ((window.I18N && window.I18N.mute_word_unmute_aria) || 'Unmute this word in exported audio')
                : ((window.I18N && window.I18N.mute_word_aria) || 'Mute this word in exported audio')
        );
    }

    function hideHoverIcon(){
        if (hoverIconEl) hoverIconEl.style.display = 'none';
    }

    function onMouseOver(e){
        var t = e.target;
        if (!t || !t.classList || !t.classList.contains('ac-ed-word')) {
            /* Allow pointer to traverse onto the icon without flicker. */
            if (t !== hoverIconEl) hideHoverIcon();
            return;
        }
        showHoverIcon(t);
    }

    function onContextMenu(e){
        var t = e.target;
        if (!t || !t.classList || !t.classList.contains('ac-ed-word')) return;
        var idx = parseInt(t.getAttribute('data-word-idx'), 10);
        if (isNaN(idx)) return;
        e.preventDefault();
        e.stopPropagation();
        api.toggleWord(idx);
    }

    /* --------- public API --------- */

    var api = {
        muteWord: function(idx){
            var st = getState();
            if (!st || !st.words || !st.words[idx]) return false;
            if (st.words[idx]._muted) return false;
            st.words[idx]._muted = true;
            repaintEditor();
            renderChip();
            if (typeof window.__aeFeature === 'function') {
                try { window.__aeFeature('mute_word_added', { idx: idx }); } catch(_){}
            }
            return true;
        },
        unmuteWord: function(idx){
            var st = getState();
            if (!st || !st.words || !st.words[idx]) return false;
            if (!st.words[idx]._muted) return false;
            delete st.words[idx]._muted;
            repaintEditor();
            renderChip();
            if (typeof window.__aeFeature === 'function') {
                try { window.__aeFeature('mute_word_removed', { idx: idx }); } catch(_){}
            }
            return true;
        },
        toggleWord: function(idx){
            var st = getState();
            if (!st || !st.words || !st.words[idx]) return false;
            if (st.words[idx]._muted) return api.unmuteWord(idx);
            return api.muteWord(idx);
        },
        reset: function(){
            var st = getState();
            if (!st || !st.words) return;
            var changed = false;
            for (var i = 0; i < st.words.length; i++){
                if (st.words[i] && st.words[i]._muted) { delete st.words[i]._muted; changed = true; }
            }
            if (changed) {
                repaintEditor();
                renderChip();
                if (typeof window.__aeFeature === 'function') {
                    try { window.__aeFeature('mute_word_reset', {}); } catch(_){}
                }
            }
        },
        hasMutes: function(){
            var words = getWords();
            for (var i = 0; i < words.length; i++) if (words[i] && words[i]._muted) return true;
            return false;
        },
        getMuteList: function(){ return collectMutes(); },
        buildFFmpegVolumeFilter: function(){ return buildVolumeFilter(); },
        installEditorHooks: function(){
            if (hooksInstalled) return;
            if (typeof document === 'undefined' || typeof window === 'undefined') return;
            hooksInstalled = true;
            injectStyles();
            var wire = function(editor){
                if (!editor) return;
                editor.addEventListener('contextmenu', onContextMenu, true);
                editor.addEventListener('mouseover', onMouseOver, true);
                editor.addEventListener('mouseout', function(e){
                    /* When pointer leaves a word but enters the icon, keep
                       the icon visible. */
                    var to = e.relatedTarget;
                    if (to === hoverIconEl) return;
                    hideHoverIcon();
                }, true);
                /* Scroll inside the editor hides the icon (its absolute
                   position would otherwise lag behind). */
                editor.addEventListener('scroll', hideHoverIcon, true);
                /* MutationObserver to repaint mute styling after the
                   edit-by-transcript module rebuilds the spans. */
                var mo = new MutationObserver(function(){ repaintEditor(); });
                mo.observe(editor, { childList: true, subtree: true });
                repaintEditor();
            };
            var editor = document.getElementById('ac-editor');
            if (editor) {
                wire(editor);
            } else {
                var tries = 0;
                var poll = setInterval(function(){
                    var e = document.getElementById('ac-editor');
                    if (e) { clearInterval(poll); wire(e); }
                    else if (++tries > 40) { clearInterval(poll); }
                }, 200);
            }
            window.addEventListener('scroll', hideHoverIcon, true);
        },
        _internals: {
            MERGE_EPSILON: MERGE_EPSILON,
            collectMutes: collectMutes,
            buildVolumeFilter: buildVolumeFilter,
        },
    };

    window.__acMuteWord = api;

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function(){ api.installEditorHooks(); }, { once: true });
        } else {
            api.installEditorHooks();
        }
    }
})();
